<template>
	<div class="change-plan-cards flex flex-col items-center">
		<CCallout v-if="isEligibleForDiscount && discountCalloutMessage != ''" type="info" icon="complex-icons/celebrate" class="change-plan-cards__discount-callout mb-4 font-bold stroke-matins text-3">
			{{ discountCalloutMessage }}<sup>*</sup>
		</CCallout>
		<CPlanCards
			show-description
			:current-plan-is-trial="currentPlanIsTrial"
			:pricing-tier="currentPricingTier"
			:public-pricing-tier="currentPublicPricing"
			is-condensed
			is-authenticated
			:is-eligible-for-discount="isEligibleForDiscount"
			:discount-eligible-plans="discountEligiblePlans"
			:allow-accordion-mode="false"
			:current-plan-name="currentPlanName"
		>
			<template #plan-card-header="plan">
				<div
					class="change-plan-cards__plan-header flex items-center justify-center font-display heavy sz-3"
					:class="{
						'bg-shade-8 text-shade-1' : plan.plan === currentPlanName,
						'bg-cta text-cta' : !isPlanEligibleForDiscount(plan.plan) && plan.plan === recommendedTier,
						'bg-green-2' : isPlanEligibleForDiscount(plan.plan)
					}"
				>
					<template v-if="plan.plan === currentPlanName">{{ currentPlanIsTrial ? 'Trial Plan' : 'Current Plan' }}</template>
					<template v-if="!isPlanEligibleForDiscount(plan.plan) && plan.plan === recommendedTier">Recommended</template>
					<template v-if="isPlanEligibleForDiscount(plan.plan)">Save 50%<sup>*</sup></template>
				</div>
			</template>
			<template #footer="{ plan }">
				<div class="plan__cta">
					<button
						v-if="(plan !== currentPlanName || currentPlanIsTrial) && showButtons"
						class="button button--primary heavy sz-4 mb-2 px-3 py-2"
						v-bind="ctaJourneyAttributes(plan)"
						@click="$router.replace(authenticatedChoosePlanLink(plan))"
					>
						<span class="heavy">Change Plan</span>
					</button>
					<button v-if="currentPlanIsTrial && plan === currentPlanName" class="mt-3 button--no-style text-link underline block mx-auto" @click="$emit('end-trial')">
						End Trial
					</button>
				</div>
			</template>
		</CPlanCards>
		<button class="block button button--no-style text-link underline mt-4" @click="$router.replace(`/${orgCode}/settings/plan/select`)">See full plan comparison</button>
		<p v-if="isEligibleForDiscount && discountCalloutMessage !== ''" class="mt-3"><small>*Limited time offer. Eligible customers may upgrade to a Pro or Team plan at a 50% discount for the first 12 months following the upgrade. After the promotional period, plans renew at the standard list price, unless cancelled or changed. This offer is non-transferable, and cannot be combined with other promotions.</small></p>
	</div>
</template>

<script lang="ts">
	import { defineComponent, onMounted, ref, computed } from 'vue';
	import CPlanCards from 'src/views/pricing/PlanCards.vue';
	import { getPricing } from '@cognitoforms/api/services/plan-service';
	import type { PricingTier } from '@cognitoforms/types/server-types/plans/model/pricing-tier';
	import CCallout from 'src/components/content/Callout.vue';
	import { startStage, startUpgradeStage } from 'src/utilities/journey/activation';
	import { reportedInterval, resolveCurrentPlanDimensions } from 'src/utilities/journey/registry';
	import { JourneyStages } from '@cognitoforms/api/services/journey-context';

	export default defineComponent({
		name: 'ChangePlanCards',
		components: {
			CPlanCards,
			CCallout
		},
		props: {
			currentPlanName: { type: String, required: true },
			currentPlanIsTrial: { type: Boolean, required: true },
			isEligibleForDiscount: { type: Boolean, required: true, default: false },
			orgCode: { type: String, required: true },
			showButtons: { type: Boolean, required: true }
		},
		emits: ['end-trial'],
		setup(props) {
			const currentPricingTier = ref({
				Individual: {
					AdditionalUserFee: '—',
					Description: '—',
					DisplayName: '—',
					EntryLimit: '—',
					FormLimit: '—',
					MonthlyAmount: '—',
					Product: { DisplayName: '—' },
					StorageLimitText: '—',
					UserLimit: '—'
				},
				Pro: {
					AdditionalUserFee: '—',
					Description: '—',
					DisplayName: '—',
					EntryLimit: '—',
					FormLimit: '—',
					MonthlyAmount: '—',
					Product: { DisplayName: '—' },
					StorageLimitText: '—',
					UserLimit: '—'
				},
				Team: {
					AdditionalUserFee: '—',
					Description: '—',
					DisplayName: '—',
					EntryLimit: '—',
					FormLimit: '—',
					MonthlyAmount: '—',
					Product: { DisplayName: '—' },
					StorageLimitText: '—',
					UserLimit: '—'
				},
				Enterprise: {
					AdditionalUserFee: '—',
					Description: '—',
					DisplayName: '—',
					EntryLimit: '—',
					FormLimit: '—',
					MonthlyAmount: '—',
					Product: { DisplayName: '—' },
					StorageLimitText: '—',
					UserLimit: '—'
				}
			});
			const currentPublicPricing = ref<PricingTier>(null);
			const isFirstPaidPlan = ref(false);
			// This componenet runs only for the old billing engine (BillingMode.Legacy), so a current
			// paid plan is always reported as legacy; an org that has never paid has no cadence to report.
			const currentInterval = computed(() => {
				if (isFirstPaidPlan.value)
					return null;
				return 'legacy';
			});
			const planDiscounts = computed(() => {
				if (!currentPublicPricing.value || !currentPricingTier.value) return [];

				const discounts = [];
				const planNames = ['Individual', 'Pro', 'Team', 'Business', 'Enterprise'];

				for (const planName of planNames) {
					const publicPrice = currentPublicPricing.value[planName]?.MonthlyAmount;
					const currentPrice = currentPricingTier.value[planName]?.MonthlyAmount;

					if (publicPrice && currentPrice && publicPrice > currentPrice) {
						const discountPercent = Math.round(((publicPrice - currentPrice) / publicPrice) * 100);
						discounts.push({ plan: planName, discountPercent });
					}
				}

				return discounts;
			});
			const discountEligiblePlans = computed(() => planDiscounts.value.map(x => x.plan));
			const getPricingTier = async () => {
				const pricing = await getPricing();
				currentPricingTier.value = pricing.currentPricingTier;
				currentPublicPricing.value = pricing.currentPublicPricing;
				isFirstPaidPlan.value = pricing.isFirstPaidPlan === true;

				if (!props.showButtons)
					return;

				const dimensions = resolveCurrentPlanDimensions(props.currentPlanName, currentInterval.value, null, null);
				if (isFirstPaidPlan.value)
					startUpgradeStage('orgsettings', undefined, false, dimensions);
				else
					startStage({ stage: JourneyStages.ChangePlan, variant: '' }, dimensions);
			};

			const recommendedTier = computed(() =>{
				return ['Team', 'Business', 'Enterprise'].includes(props.currentPlanName) ? '' : 'Team';
			});

			const isPlanEligibleForDiscount = (planName: 'Individual' | 'Pro' | 'Team' | 'Business' | 'Enterprise') => {
				return props.isEligibleForDiscount && props.currentPlanName === 'Individual' && discountEligiblePlans?.value.includes(planName);
			};

			const discountCalloutMessage = computed(() => {
				if (!planDiscounts.value?.length) return '';

				// Check if all eligible plans have the same discount percentage
				const uniqueDiscounts = [...new Set(planDiscounts.value.map(p => p.discountPercent))];
				if (uniqueDiscounts.length !== 1) {
					// Fallback for mixed discount percentages
					return 'You’re eligible for a limited time discount on select plans.';
				}

				const discountPercent = uniqueDiscounts[0];
				const planNames = planDiscounts.value.map(p => p.plan);
				let planText;
				switch (planNames.length) {
				case 1:
					planText = `the ${planNames[0]} plan`;
					break;
				case 2:
					planText = `the ${planNames.join(' or ')} plan`;
					break;
				default:
					planText = `${planNames.slice(0, -1).join(', ')}, or ${planNames[planNames.length - 1]} plans`;
					break;
				}

				return `You’re eligible for a limited time offer. Save ${discountPercent}% on ${planText}.`;
			});

			const authenticatedChoosePlanLink = (plan) => {
				return '/' + props.orgCode + '/settings/plan/subscription?plan=' + encodeURIComponent(plan) +
					'&prepaymonths=0&prepayusers=0&prepaytemplates=0';
			};

			const ctaJourneyAttributes = (plan: string) => {
				let stage: Record<string, string>;
				if (isFirstPaidPlan.value)
					stage = { 'data-cf-step': 'ConvertToPaidCtaClicked', 'data-cf-stage': 'ConvertToPaid' };
				else
					stage = { 'data-cf-step': 'ChangePlanCtaClicked', 'data-cf-stage': 'ChangePlan' };

				return {
					...stage,
					'data-cf-target-plan': plan,
					'data-cf-target-interval': reportedInterval('legacy'),
					...resolveCurrentPlanDimensions(props.currentPlanName, currentInterval.value, null, null)
				};
			};

			onMounted(()=>{
				getPricingTier();
			});

			return {
				currentPricingTier,
				currentPublicPricing,
				recommendedTier,
				authenticatedChoosePlanLink,
				ctaJourneyAttributes,
				getPricingTier,
				isPlanEligibleForDiscount,
				discountEligiblePlans,
				discountCalloutMessage
			};
		}
	});
</script>

<style lang="scss">
.change-plan-cards {
	container: change-plan-cards / inline-size;

	$card_wrap_breakpoint: 726px;

	&__discount-callout {
		width: 100%;

		.callout {

			&__icon {
				width: 38px;
				height: 38px;
			}

			&__inner-container {
				display: flex;
				align-items: center;
			}
		}
	}

	&__plan-header {
		height: 30px;

		&.bg-shade-8 + .plan {
			background-color: var(--shade-4);
			box-shadow: unset;
		}

		/* stylelint-disable-next-line scss/at-rule-no-unknown */
		@container change-plan-cards (max-width: #{$card_wrap_breakpoint}) {

			&:not(.bg-accent-7, .bg-shade-8) {
				display: none;
			}
		}
	}

	.plans {
		gap: $space__4;
		width: 100%;

		/* stylelint-disable-next-line scss/at-rule-no-unknown */
		@container change-plan-cards (max-width: 895px) {
			gap: $space__3;
		}

		/* stylelint-disable-next-line scss/at-rule-no-unknown */
		@container change-plan-cards (max-width: #{$card_wrap_breakpoint}) {
			flex-wrap: wrap;

			&__card-container {
				width: calc(50% - $space__3);
			}
		}

		/* stylelint-disable-next-line scss/at-rule-no-unknown */
		@container change-plan-cards (max-width: 415px) {

			&__card-container {
				width: 100%;

				.plan__price {
					min-height: 3.5rem;

					&-amount {
						font-size: 3.5rem;
					}
				}
			}
		}

		&__card-container {
			display: flex;
			flex-direction: column;
			border: none;

			.plan {
				flex-grow: 1;
			}
		}

		.bg-accent-7 + .plan {
			border-color: var(--border-selected-intense);
		}
	}

	.plans__card-container {

		&:not(.plans__card-container--no-header) {
			border: 1px solid var(--border-primary);
			box-shadow: 1px 1px 4px 0 var(--shadow-color-intense), 2px 2px 8px 0 var(--shadow-color);
		}

		&--no-header .plan {
			border: 1px solid var(--border-primary);
			box-shadow: 1px 1px 4px 0 var(--shadow-color-intense), 2px 2px 8px 0 var(--shadow-color);
		}
	}

	.plan {
		height: unset;

		&--selected {
			border-color: var(--border-selected-intense);
		}

		&__card {
			padding: $space__4 0;
		}

		&__title {
			margin-bottom: $space__3;
			font-family: var(--font-display);
			font-size: $size__5;
		}

		:root &__description {
			padding: 0 $space__4;
			font-size: $size__1;
		}

		&--individual .plan__description {
			padding: 0 $space__5;
		}

		&__cta {
			min-height: unset;

			.button {
				background-color: var(--bg-cta);
				padding: $space__3 $space__4;
				font-family: var(--font-display);
			}
		}

		&__price--suffix {
			display: none;

			@container change-plan-cards (max-width: #{$card_wrap_breakpoint}) {
				display: inline;
			}
		}
	}
}
</style>